import { supabase } from '../../../shared/lib/supabase';

export interface Organization {
  id: string;
  name: string;
  subdomain: string;
  logo: string;
  created_at: string;
  updated_at: string;
  active: boolean;
  allowed_domains: string[];
  allowed_ips: string[];
  require_api_whitelist: boolean;
}

export interface OrganizationCreateInput {
  name: string;
  subdomain: string;
  logo?: string;
  active?: boolean;
  allowed_domains?: string[];
  allowed_ips?: string[];
  require_api_whitelist?: boolean;
}

export interface OrganizationUpdateInput {
  name?: string;
  subdomain?: string;
  logo?: string;
  active?: boolean;
  allowed_domains?: string[];
  allowed_ips?: string[];
  require_api_whitelist?: boolean;
}

export class OrganizationsService {
  private tableName = 'organisations';

  async getAll(): Promise<Organization[]> {
    try {
      const { data, error } = await supabase
        .from(this.tableName)
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching organizations:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
      throw error;
    }
  }

  async getById(id: string): Promise<Organization | null> {
    try {
      const { data, error } = await supabase
        .from(this.tableName)
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        console.error('Error fetching organization:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Failed to fetch organization:', error);
      throw error;
    }
  }

  async create(input: OrganizationCreateInput): Promise<Organization> {
    try {
      const { data, error } = await supabase
        .from(this.tableName)
        .insert([input])
        .select()
        .single();

      if (error) {
        console.error('Error creating organization:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Failed to create organization:', error);
      throw error;
    }
  }

  async update(id: string, input: OrganizationUpdateInput): Promise<Organization> {
    try {
      const { data, error } = await supabase
        .from(this.tableName)
        .update({
          ...input,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating organization:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Failed to update organization:', error);
      throw error;
    }
  }

  async delete(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from(this.tableName)
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting organization:', error);
        throw error;
      }
    } catch (error) {
      console.error('Failed to delete organization:', error);
      throw error;
    }
  }

  async export(): Promise<string> {
    try {
      const { data, error } = await supabase
        .from(this.tableName)
        .select('*');

      if (error) {
        console.error('Error exporting organizations:', error);
        throw error;
      }

      // Convert to CSV
      const csvData = this.convertToCSV(data || []);
      return csvData;
    } catch (error) {
      console.error('Failed to export organizations:', error);
      throw error;
    }
  }

  private convertToCSV(data: Organization[]): string {
    if (data.length === 0) return '';

    const headers = [
      'id',
      'name', 
      'subdomain',
      'logo',
      'created_at',
      'updated_at',
      'active',
      'allowed_domains',
      'allowed_ips',
      'require_api_whitelist'
    ];

    const csvRows = [
      // Headers
      headers.join(','),
      // Data rows
      ...data.map(row => [
        `"${row.id}"`,
        `"${row.name}"`,
        `"${row.subdomain}"`,
        `"${row.logo}"`,
        `"${row.created_at}"`,
        `"${row.updated_at}"`,
        row.active ? 'true' : 'false',
        `"${JSON.stringify(row.allowed_domains)}"`,
        `"${JSON.stringify(row.allowed_ips)}"`,
        row.require_api_whitelist ? 'true' : 'false'
      ].join(','))
    ];

    return csvRows.join('\n');
  }
}

// Export singleton instance
export const organizationsService = new OrganizationsService();
